
import React, { useEffect } from 'react';
import { RotateCcw, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import { useArticleAI } from '@/contexts/article-ai/ArticleAIContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface ArticleAlternativePerspectiveProps {
  article: any;
}

const ArticleAlternativePerspective: React.FC<ArticleAlternativePerspectiveProps> = ({ article }) => {
  const { 
    generateAlternativePerspective, 
    alternativePerspective, 
    isPerspectiveLoading,
    provideFeedback
  } = useArticleAI();

  useEffect(() => {
    if (!alternativePerspective && !isPerspectiveLoading) {
      generateAlternativePerspective();
    }
  }, [alternativePerspective, isPerspectiveLoading, generateAlternativePerspective]);

  return (
    <Card className="w-full border-primary/20 shadow-md">
      <CardHeader className="pb-2 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-950/30 dark:to-orange-950/30 rounded-t-md">
        <div className="flex items-center">
          <RotateCcw className="h-5 w-5 mr-2 text-orange-600 dark:text-orange-400" />
          <CardTitle className="text-lg text-orange-700 dark:text-orange-300">Different Perspective</CardTitle>
        </div>
        <CardDescription className="text-orange-600/80 dark:text-orange-400/80">
          An alternative viewpoint on this topic
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        {isPerspectiveLoading ? (
          <div className="py-6 flex justify-center items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
          </div>
        ) : alternativePerspective ? (
          <div className="space-y-3 py-1">
            <div className="p-3 bg-yellow-50 dark:bg-yellow-950/30 rounded-md border border-yellow-200 dark:border-yellow-800">
              <div className="flex items-start">
                <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-400 mb-1">DISCLAIMER</p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-500">
                    This content represents an alternative perspective generated by AI. 
                    It is provided for balance and does not necessarily reflect the views of the author.
                  </p>
                </div>
              </div>
            </div>
            <div className="whitespace-pre-wrap bg-white dark:bg-gray-900 p-3 rounded-md border border-gray-100 dark:border-gray-800 shadow-inner">
              {alternativePerspective}
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-4">Unable to generate alternative perspective</p>
        )}
      </CardContent>
      <Separator className="bg-orange-100 dark:bg-orange-900/30" />
      <CardFooter className="pt-2 flex justify-between bg-gradient-to-r from-yellow-50/50 to-orange-50/50 dark:from-yellow-950/20 dark:to-orange-950/20 rounded-b-md">
        <div className="text-xs text-muted-foreground">
          Generated by AI to provide balanced viewpoints
        </div>
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 hover:bg-orange-100 dark:hover:bg-orange-900/20" 
            title="This was helpful"
            onClick={() => provideFeedback('perspective', 'perspective', 'helpful')}
          >
            <ThumbsUp className="h-3 w-3 text-orange-600 dark:text-orange-400" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7 hover:bg-red-100 dark:hover:bg-red-900/20" 
            title="This wasn't helpful"
            onClick={() => provideFeedback('perspective', 'perspective', 'not-helpful')}
          >
            <ThumbsDown className="h-3 w-3 text-red-500" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default ArticleAlternativePerspective;
